import type { AgentData } from './User';

// TODO: Fix types
export type ChatWindowTitle = {
  title: string;
  subtitle: string;
  avatarPath: string;
  avatarType?: string;
  headerType: 'slim' | 'normal' | 'noimage';
  headerTitleColor: string;
  headerColor: string;
  showCloseButton: boolean;
};

export type MessageBoxState = {
  text: string;
  textareaContent: string;
  showMenu: boolean;
  showWaitingForTheBotResponseMessage: boolean;
};

export interface ISendChatStatusData {
  status: ChatActivityStatus;
}

export type ChatActivityStatus = 'online' | 'offline' | 'idle';

export type MessageBoxProps = {
  submitHandler: (...args: any[]) => void;
  attachments: Array<Attachment>;
  setAttachments: (...args: any[]) => void;
  allowTextInput: boolean;
  messages: Array<Message>;
  setSendChatStatus: (statusData: ISendChatStatusData) => void;
  changeChatActivityStatus: (status: ChatActivityStatus) => void;
};

export type Attachment = {
  size: number;
  name: string;
  base64: string;
  type: string;
};

export type UrlOpenMethod = 'tab' | 'window' | 'same';

export type Button = {
  title: string;
  type?: 'webLink' | 'payload';
  payload: string;
  url?: string;
  options?: {
    allowTextInput?: boolean;
    webclient?: {
      urlOpenMethod?: UrlOpenMethod;
      close?: boolean;
    };
  };
};

export type ReplaceTextURL = {
  [key: string]: Array<string> | string;
};

export type TextData = {
  text: string;
  replaceTextUrl?: ReplaceTextURL; // TODO: Replace with options
};

export type Text = {
  type: 'text';
  data: TextData;
  options?: {
    webclient?: TWebclientOptions;
    agentData?: AgentData;
    sentBy?: Sender;
  };
  flowState?: string;
};

export type SystemMessage = {
  type: 'system';
  data: SystemData;
};

export type SystemData = {
  text: string;
};

export type ImageData = {
  url: string;
};
export type ImageUrl = {
  type: 'image';
  data: ImageData;
};

export type RatioData = 'square';
export type CarouselElement = {
  title: string;
  subtitle: string;
  image_url: string;
  buttons: Array<Button>;
};
export type CarouselData = {
  ratio: RatioData;
  elements: CarouselElement[];
};
export type CarouselOptions = {
  templateType?: 'list' | 'carousel';
};
export type Carousel = {
  type: 'carousel';
  data: CarouselData;
  options?: {
    webclient?: CarouselOptions & TWebclientOptions;
  };
};

export type End = {
  type: 'end';
};

export type MessageWithButtonsData = {
  text: string;
  replaceTextUrl?: ReplaceTextURL;
  buttons: Array<Button>;
  llmUsageId?: string;
};

export type MessageWithButtons = {
  type: 'textWithButtons';
  data: MessageWithButtonsData;
  options?: {
    webclient?: TWebclientOptions;
  };
  flowState?: string;
};

export type ImageWithButtonsData = {
  image: string;
  buttons: Array<Button>;
};
export type ImageWithButtons = {
  type: 'imageWithButtons';
  data: ImageWithButtonsData;
};

export type CredentialData = {
  cred: string;
};
export type Credential = {
  type: 'auth';
  data: CredentialData;
};

export type Action = {
  type: 'action';
  data: 'typing_on' | 'typing_off';
};

export type DataRequest = {
  type: 'dataRequest';
  data: string;
};

export type UpdateUserId = {
  type: 'updateUserId';
  data: string;
};

export type IdleOptions = {
  type: 'idleOptions';
  data: {
    idleTimeout: number;
    closeAfterIdleTimeout: boolean;
  };
};

export type FlowState = {
  type: 'flowState';
  data: {
    state: string;
  };
};

export type IDData = {
  id: string;
};
export type ID = {
  type: 'id';
  data: IDData;
};

export type RatingData = {
  submitText: string;
  minRating: number;
  maxRating: number;
  rating?: number;
};

export type Rating = {
  type: 'feedback';
  data: RatingData;
};

export type TWebclientOptions = {
  allowTextInput?: boolean; // DEPRECATED: since we introduced the new buttonInteractionOnly field for specific blocks, but because of the old configs, it has to be kept until they are rewritten.
  urlReplaceText?: string[];
  buttonInteractionOnly?: boolean;
  close?: boolean;
};

export type TOptions = {
  options?: TWebclientOptions;
};

export type TDropdownPayload = {
  type: 'dropdown';
  data: {
    description: string;
    submitButtonText: string;
    content: { id: string; name: string; value: string }[];
  };
};

export type MessagePayload =
  | Text
  | ImageUrl
  | Carousel
  | MessageWithButtons
  | ImageWithButtons
  | Credential
  | Action
  | ID
  | DataRequest
  | UpdateUserId
  | TDropdownPayload
  | Rating
  | End
  | IdleOptions
  | FlowState
  | SystemMessage;

export type MessageDirection = 'in' | 'out' | 'system';

export type Message = {
  direction: MessageDirection;
  payload: MessagePayload;
};

export type ChatBubbleInfo = {
  avatarImg: string;
  agentName?: string;
};

export type ChatHistoryPayload = Record<string, unknown>;
export type ChatHistory =
  | TextChat
  | ImageChat
  | TextWithButtonsChat
  | ImageCarouselChat
  | ButtonChat
  | CredentialChat
  | RatingChat
  | EndChat
  | SystemChat;

export type TextChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  direction: MessageDirection;
  time: Date;
  message: {
    content: string;
    replaceTextUrl?: ReplaceTextURL;
    options?: {
      webclient: TWebclientOptions;
    };
  };
  messageType: 'text';
};

export type ImageChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  direction: MessageDirection;
  time: Date;
  message: {
    content: {
      url: string;
    };
  };
  messageType: 'image';
};

export type RatingChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  direction: MessageDirection;
  time: Date;
  content: {
    submitText: string;
    minRating: number;
    maxRating: number;
    rating: number;
  };
  messageType: 'feedback';
};

export enum Sender {
  AI = 'AI',
  Agent = 'Agent',
  Bot = 'Bot',
}

export type TextWithButtonsChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  direction: MessageDirection;
  time: Date;
  options?: {
    webclient?: {
      allowTextInput?: boolean; // DEPRECATED: since we introduced the new buttonInteractionOnly field for specific blocks, but because of the old configs, it has to be kept until they are rewritten.
      buttonInteractionOnly?: boolean;
    };
    sentBy?: Sender;
  };
  content: {
    buttons: Button[];
    text: string;
    replaceTextUrl?: ReplaceTextURL;
    llmUsageId?: string;
  };
  messageType: 'textWithButtons' | 'text-v2';
};

export type ImageCarouselChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  options?: {
    webclient?: { templateType: 'list' | 'carousel' };
  };
  direction: MessageDirection;
  time: Date;
  content: CarouselElement[];
  messageType: 'imageCarousel';
};

export type ButtonChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  direction: MessageDirection;
  formattedValue?: string;
  content: string;
  time: Date;
  messageType: 'button';
};

export type CredentialChat = {
  blockId?: string;
  senderType: string;
  senderId?: string;
  direction: MessageDirection;
  formattedValue?: string;
  content: string;
  time: Date;
  messageType: 'credential';
};

export type EndChat = {
  direction: MessageDirection;
  time: Date;
  messageType: 'end';
};

export type SystemChat = {
  direction: MessageDirection;
  time: Date;
  messageType: 'system';
  message: {
    content: string;
  };
};

export type SystemChatContent = {
  message: string;
};
